import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { useSite } from "@/context/SiteContext";
import { telLink, waLink, mailLink } from "@/lib/api";

export default function Footer() {
  const { settings, products } = useSite();
  const showAddress = settings.address && !settings.address.startsWith("[INSERT");

  return (
    <footer data-testid="site-footer" className="bg-brand-ink text-white/75 relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-20" />
      <div className="container-site relative py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Logo light />
          <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-xs">
            Quality-focused agricultural seed solutions for farmers and agricultural partners across Bihar.
          </p>
        </div>
        <div>
          <h4 className="font-display font-bold text-white text-sm tracking-wider uppercase mb-4">Quick Links</h4>
          <ul className="space-y-2.5 text-sm">
            {[["Home", "/"], ["About Us", "/about"], ["Products", "/products"], ["Become a Partner", "/partner"], ["Farm Knowledge", "/knowledge"], ["Contact", "/contact"]].map(([label, to]) => (
              <li key={to}>
                <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-brand-wheat transition-colors">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-white text-sm tracking-wider uppercase mb-4">Products</h4>
          <ul className="space-y-2.5 text-sm">
            {products.map((p) => (
              <li key={p.slug}>
                <Link to={`/products/${p.slug}`} data-testid={`footer-product-${p.slug}`} className="hover:text-brand-wheat transition-colors">
                  {p.name} <span className="text-white/40">· {p.crop}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-bold text-white text-sm tracking-wider uppercase mb-4">Contact</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={telLink(settings)} data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-brand-wheat transition-colors">
                <Phone className="w-4 h-4 text-brand-gold" /> {settings.phone}
              </a>
            </li>
            <li>
              <a
                href={waLink(settings, "Hello ICROPSIA team, I would like to know more about your seeds.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-2.5 hover:text-brand-wheat transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-brand-gold" /> WhatsApp Us
              </a>
            </li>
            <li>
              <a href={mailLink(settings)} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-brand-wheat transition-colors">
                <Mail className="w-4 h-4 text-brand-gold" /> {settings.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5 text-white/60">
              <MapPin className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
              <span>{showAddress ? settings.address : settings.region_text || "Bihar, India"}</span>
            </li>
            {settings.regional_address && (
              <li className="flex items-start gap-2.5 text-white/60">
                <MapPin className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" />
                <span><span className="text-brand-wheat font-semibold">Regional Office:</span> {settings.regional_address}</span>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="container-site py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/45">
          <span>© {new Date().getFullYear()} ICROPSIA India Pvt Ltd. All Rights Reserved.</span>
          <div className="flex gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-brand-wheat transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-brand-wheat transition-colors">Terms &amp; Conditions</Link>
            <Link to="/disclaimer" data-testid="footer-disclaimer" className="hover:text-brand-wheat transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
